import { Suspense, lazy, useCallback, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Accordion from '@components/ui/Accordion/Accordion';
import { Button } from '@components/ui';
import { useTranslation } from '@hooks/useTranslation';
import type { FormTemplateFormEntry } from '@/types/formTemplate';
import type { FormConfigSummary } from '@/types/form';
import EmployeeFormSettingsPanel from './EmployeeFormSettingsPanel';
import styles from './EmployeeFormEntryCard.module.scss';

const FormPreview = lazy(() => import('../tenant-templates/FormPreview'));

// ============================================
// TYPES
// ============================================

export interface FormEntryWithId extends FormTemplateFormEntry {
  /** Client-side ID for DnD tracking (not sent to API) */
  _clientId: string;
}

interface EmployeeFormEntryCardProps {
  entry: FormEntryWithId;
  index: number;
  onChange: (index: number, updated: FormEntryWithId) => void;
  onRemove: (index: number) => void;
  formConfigs: FormConfigSummary[];
}

// ============================================
// GRIP ICON
// ============================================

function GripIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="4" cy="2" r="1" fill="currentColor" />
      <circle cx="8" cy="2" r="1" fill="currentColor" />
      <circle cx="4" cy="6" r="1" fill="currentColor" />
      <circle cx="8" cy="6" r="1" fill="currentColor" />
      <circle cx="4" cy="10" r="1" fill="currentColor" />
      <circle cx="8" cy="10" r="1" fill="currentColor" />
    </svg>
  );
}

// ============================================
// COMPONENT
// ============================================

function EmployeeFormEntryCard({
  entry,
  index,
  onChange,
  onRemove,
  formConfigs,
}: EmployeeFormEntryCardProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry._clientId });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const selectedFormName = useMemo(
    () => formConfigs.find((fc) => fc.id === entry.formConfigId)?.formName ?? t('K_LABEL_FORM_NUMBER', 'Form {number}').replace('{number}', String(index + 1)),
    [formConfigs, entry.formConfigId, index, t],
  );

  const handleSettingsChange = useCallback(
    (updated: FormTemplateFormEntry) => {
      onChange(index, { ...updated, _clientId: entry._clientId });
    },
    [index, entry._clientId, onChange],
  );

  const handleRemove = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  return (
    <div ref={setNodeRef} style={sortableStyle} className={styles.root}>
      <Accordion
        title={selectedFormName}
        variant="filled"
        whiteBody
        defaultExpanded
        className={styles.accordion}
        icon={
          <span
            className={styles.grip}
            {...attributes}
            {...listeners}
          >
            <GripIcon />
          </span>
        }
        action={
          <Button
            variant="danger"
            size="sm"
            onClick={handleRemove}
            className={styles.removeButton}
          >
            −
          </Button>
        }
      >
        <EmployeeFormSettingsPanel
          entry={entry}
          onChange={handleSettingsChange}
        />
        <div className={styles.previewSection}>
          <h4 className={styles.previewHeader}>{t('K_LABEL_FORM_PREVIEW', 'Form Preview')}</h4>
          <Suspense fallback={<div>{t('K_LOADING_PREVIEW', 'Loading preview...')}</div>}>
            <FormPreview formConfigId={entry.formConfigId} />
          </Suspense>
        </div>
      </Accordion>
    </div>
  );
}

export default EmployeeFormEntryCard;
